import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utils/axios';

export interface BenchmarkPoint {
  date: string;
  close: number;
}

export interface PerTickerRow {
  ticker: string;
  trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_pnl: number;
  avg_pnl: number;
  avg_hold_seconds: number | null;
}

export interface PnlHistogramBin {
  bin_start: number;
  bin_end: number;
  count: number;
}

export interface HoldTimeBucket {
  bucket: string;
  count: number;
}

export interface Distributions {
  pnl_histogram: PnlHistogramBin[];
  hold_time_distribution: HoldTimeBucket[];
  total_sells: number;
}

export interface RegimePoint {
  date: string;
  close: number;
  ma: number | null;
}

export interface RegimeCurrent {
  close: number;
  ma: number | null;
  above: boolean | null;
}

export interface RegimeIndex {
  symbol: string;
  data: RegimePoint[];
  current: RegimeCurrent | null;
}

interface PanelState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Shared fetch driver for the three panel hooks. `enabled` gates fetching
// (e.g. skip when traderId is invalid); `extract` maps the axios response to
// the state value. A useRef sequence counter discards out-of-order responses.
function usePanelFetch<T>(
  enabled: boolean,
  fetcher: () => Promise<{ data: any }>,
  extract: (payload: any) => T,
  fallbackMessage: string
): PanelState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchSeq = useRef(0);

  const run = useCallback(async () => {
    if (!enabled) return;
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetcher();
      if (seq !== fetchSeq.current) return; // stale response; a newer request is in flight
      setData(extract(response.data));
      setError(null);
      setLoading(false);
    } catch (err: any) {
      if (seq !== fetchSeq.current) return;
      const message = err.response?.data?.detail || err.message || fallbackMessage;
      setError(message);
      setData(null);
      setLoading(false);
    }
    // fetcher/extract are recreated per render by the calling hook's useCallback,
    // which already depends on the underlying params; enabled/fallbackMessage cover the rest.
  }, [enabled, fetcher, extract, fallbackMessage]);

  useEffect(() => {
    run();
  }, [run]);

  return { data, loading, error, refetch: run };
}

export function useBenchmark(symbol: string, startDate?: string, endDate?: string) {
  const fetcher = useCallback(
    () => axios.get('/benchmark', { params: { symbol, start_date: startDate, end_date: endDate } }),
    [symbol, startDate, endDate]
  );
  const extract = useCallback((payload: any): BenchmarkPoint[] => payload.data, []);
  return usePanelFetch<BenchmarkPoint[]>(true, fetcher, extract, 'Failed to fetch benchmark');
}

export function useDeposits(traderId: number) {
  const fetcher = useCallback(
    () => axios.get('/portfolio', { params: { trader_id: traderId } }),
    [traderId]
  );
  const extract = useCallback((payload: any): number | null => payload?.Deposits ?? null, []);
  return usePanelFetch<number | null>(traderId > 0, fetcher, extract, 'Failed to fetch portfolio');
}

export function usePerTicker(traderId: number, startDate?: string, endDate?: string) {
  const fetcher = useCallback(
    () =>
      axios.get('/statistics/per-ticker', {
        params: { trader_id: traderId, start_date: startDate, end_date: endDate },
      }),
    [traderId, startDate, endDate]
  );
  const extract = useCallback((payload: any): PerTickerRow[] => payload.tickers, []);
  return usePanelFetch<PerTickerRow[]>(traderId > 0, fetcher, extract, 'Failed to fetch per-ticker statistics');
}

export function useDistributions(traderId: number, startDate?: string, endDate?: string) {
  const fetcher = useCallback(
    () =>
      axios.get('/statistics/distributions', {
        params: { trader_id: traderId, start_date: startDate, end_date: endDate },
      }),
    [traderId, startDate, endDate]
  );
  const extract = useCallback(
    (payload: any): Distributions => ({
      pnl_histogram: payload.pnl_histogram,
      hold_time_distribution: payload.hold_time_distribution,
      total_sells: payload.total_sells,
    }),
    []
  );
  return usePanelFetch<Distributions>(traderId > 0, fetcher, extract, 'Failed to fetch distributions');
}

export function useRegime() {
  const fetcher = useCallback(() => axios.get('/regime'), []);
  const extract = useCallback((payload: any): RegimeIndex[] => payload.indices, []);
  return usePanelFetch<RegimeIndex[]>(true, fetcher, extract, 'Failed to fetch market regime');
}
